import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import SEO from "@/components/SEO";

const faqs = [
  {
    q: "What is an Asian Handicap?",
    a: "The Asian Handicap is a form of football (soccer) betting where one team is given a virtual head-start or deficit before the match begins. Instead of a simple win/draw/lose (1X2) market, Asian Handicap gives you only two outcomes on any given line, which cuts the bookmaker's built-in edge dramatically. Handicaps come in whole numbers (e.g. -1, +2), half-numbers (-0.5, +1.5) and quarter-numbers (-0.25, +0.75) — the quarter lines split your stake between two adjacent handicaps.",
  },
  {
    q: "Why do you only focus on the Asian Handicap market?",
    a: "Because it's the fairest, most liquid market in football and the one where sharp bettors have historically found the most repeatable edge. It removes the 'draw tax' baked into 1X2 markets and gives us clean, two-way outcomes to model. Specialising in one market lets our analysts go deeper on each pick instead of spreading effort thin across dozens of secondary markets.",
  },
  {
    q: "How many tips will I receive per week?",
    a: "It depends on the fixture calendar and, more importantly, on where we find genuine value. As a rough guide, our Pro (30 AH TIPS) package usually plays out over four to six weeks of European club football. We never force out picks to hit a quota — that's the fastest way to torch a win rate.",
  },
  {
    q: "What odds range do your picks sit in?",
    a: "Every published Asian Handicap tip is priced between 1.85 and 2.20 at the recommended bookmaker at time of release. This is the sweet-spot range where AH markets are most efficient and where our long-term edge is documented. We do not chase high-odds specials.",
  },
  {
    q: "How do I know your track record is real?",
    a: "Because it's fully public. Our Predictions Archive lists every single tip we've released — with the exact match, competition, handicap line, odds, final score and settled outcome. Nothing gets edited, hidden or removed after the fact. Browse it, count the wins and losses yourself, then decide if the record is worth subscribing to.",
  },
  {
    q: "How do I subscribe? I don't see a payment button.",
    a: "By design. We do not process payments on this website. To subscribe, use our Contact page or email asianhpicks@gmail.com with the package you want (15, 30 or 50 AH tips) and your preferred payment method. We'll reply within 24 hours with payment details and start delivering tips as soon as the payment clears.",
  },
  {
    q: "Which payment methods do you accept?",
    a: "We accept Skrill, Neteller, PayPal, Payoneer, Perfect Money, Revolut and major cryptocurrencies (BTC, ETH, USDT). Bank transfers are available on request. Exact wallet addresses and account details are shared securely once you initiate contact.",
  },
  {
    q: "How are tips delivered to me?",
    a: "By email, straight after the analyst confirms the pick. Each email contains the match, the Asian Handicap line, the recommended odds, a suggested stake as a percentage of bankroll, and a short reasoning paragraph. Priority delivery on the Pro and VIP packages means you get the pick early, before the market moves against the price.",
  },
  {
    q: "Do you offer refunds or free replacements for losing tips?",
    a: "No, and we're upfront about why: no honest tipster can guarantee wins. Losing picks are part of a long-term winning strategy — our published 82% win rate already includes every loss and push. What we do promise is discipline, transparency and no hidden games. If you want a service that pretends losses don't happen, we're not it.",
  },
  {
    q: "Can I use your tips with any bookmaker?",
    a: "Yes. Every tip includes the recommended entry line and price. As long as your bookmaker offers Asian Handicap markets (Pinnacle, SBOBET, Bet365 Asian, most Asian-facing books), you can follow along. Line-shopping across two or three books will squeeze out a bit more long-term value.",
  },
  {
    q: "Is this legal in my country?",
    a: "That depends entirely on your local gambling laws. Asian Picks is an information and analysis service — we do not accept wagers. It is your responsibility to check that sports betting is legal in your jurisdiction before placing any bet based on our content.",
  },
  {
    q: "I have a gambling problem. Can you still send me tips?",
    a: "No. If you feel that betting is affecting your finances, relationships or mental health, please stop immediately and reach out to BeGambleAware.org or GamblingTherapy.org for confidential support. If you contact us about a subscription and mention concerns about problem gambling, we will politely decline and point you toward help resources.",
  },
];

export default function FAQ() {
  return (
    <div data-testid="faq-page">
      <SEO
        title="Asian Handicap Tips FAQ — Everything You Need to Know"
        description="Answers to the most common questions about Asian Handicap betting, our tipping service, subscription packages, payment methods, and the 81.1% verified win rate."
        path="/faq"
        keywords="Asian Handicap FAQ, AH betting explained, football tipster FAQ, how to subscribe AH tips"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      <section className="bg-ink-950 text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-emerald-700/25 blur-[120px]" />
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-gold-500/15 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gold-400 font-mono uppercase tracking-widest text-xs mb-3">
            FAQ
          </div>
          <h1
            data-testid="faq-title"
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl"
          >
            Everything worth <span className="gold-text">asking</span> before you subscribe.
          </h1>
          <p className="mt-5 max-w-2xl text-white/70 text-base sm:text-lg leading-relaxed">
            Straight answers to the questions our members and prospective
            members ask most often. Still not sure? The Contact page is one
            click away.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-ink-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Accordion type="single" collapsible data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-ink-200"
              >
                <AccordionTrigger className="text-left text-base sm:text-lg font-display font-semibold text-ink-900 hover:no-underline hover:text-emerald-700 py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-ink-500 text-sm sm:text-base leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
